import SearchBar from '-/Custom/SearchBar';
import { Stack, useRouter } from 'expo-router';
import { useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { TextInput } from 'react-native';
import type { ChatCreateGroupFormValues } from '@/components/Chat/ChatCreateGroup.types';
import FormCheckboxItem from '@/components/Custom/FormCheckboxItem';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import * as PersonListItem from '@/components/Person/PersonListItem';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import List from '@/components/Unistyles/List';
import { graphql, readFragment } from '@/gql/graphql';
import type { PersonBoolExp } from '@/gql/scalars';
import useSearchablePaginatedQuery from '@/hooks/useSearchablePaginatedQuery';
import useUserId from '@/hooks/useUserId';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';

const GetPeople = graphql(/* GraphQL */ `
  query GetChatCreateGroupPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

const SearchPeople = graphql(/* GraphQL */ `
  query SearchChatCreateGroupPeople(
    $where: person_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    person(
      where: $where
      limit: $limit
      offset: $offset
      order_by: { name: asc }
    ) {
      id
      ...PersonListItemAvatar
      ...PersonListItemName
    }
  }
`, [
  PersonListItem.PersonListItemAvatarFragment,
  PersonListItem.PersonListItemNameFragment,
]);

export default function ChatCreateGroup() {
  const userId = useUserId();
  const { push } = useRouter();

  const form = useFormContext<ChatCreateGroupFormValues>();
  const searchTerm = useWatch({
    control: form.control,
    name: 'searchTerm',
    defaultValue: '',
  });

  const selectedPeople = useWatch({
    control: form.control,
    name: 'people',
    defaultValue: [],
  });

  const searchInputRef = useRef<TextInput>(null);

  const filteredIds = [userId];

  const baseWhere: PersonBoolExp = {
    _and: [
      { contact_access: { viewer_person_id: { _eq: userId } } },
      { is_support: { _eq: false } },
      filteredIds.length ? { id: { _nin: filteredIds } } : {},
    ],
  };

  const searchWhere: PersonBoolExp = {
    _and: [
      baseWhere,
      {
        name: {
          _ilike: `%${searchTerm}%`,
        },
      },
    ],
  };

  const { items, fetching } = useSearchablePaginatedQuery({
    searchTerm,
    searchOnMinimum: DEFAULT_SEARCH_MINIMUM,
    pageSize: 25,
    getQuery: GetPeople,
    getVariables: ({ limit, offset }) => ({
      where: baseWhere,
      limit,
      offset,
    }),
    selectGetItems: (d) => d?.person ?? [],
    searchQuery: SearchPeople,
    searchVariables: ({ searchTerm: term }) => ({
      where: term.length >= DEFAULT_SEARCH_MINIMUM ? searchWhere : baseWhere,
      limit: 25,
      offset: 0,
    }),
    selectSearchItems: (d) => d?.person ?? [],
  });

  return (
    <Box flex>
      <Stack.Screen options={{ title: 'New Group Chat' }} />

      <Box p="lg" space="md">
        <Text size="xl">{selectedPeople.length} Members Selected</Text>
        <Text variant="typographySecondary">
          Select at least 2 people to start a group chat.
        </Text>
        <SearchBar
          ref={searchInputRef}
          value={searchTerm}
          onChangeText={(next) =>
            form.setValue('searchTerm', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </Box>
      <List
        renderScrollComponent={(props) => (
          <ModalKeyboardAwareScrollView {...props} />
        )}
        data={items}
        fetching={fetching}
        renderItem={({ item }) => {
          const person = readFragment(
            PersonListItem.PersonListItemNameFragment,
            item,
          );

          return (
            <FormCheckboxItem
              accessibilityLabel={person.name || 'Person'}
              name="people"
              item={item}
              getItemId={(person) => person.id}
            >
              <PersonListItem.Container>
                <PersonListItem.Avatar fragment={item} />
                <PersonListItem.Content>
                  <PersonListItem.Name fragment={item} />
                </PersonListItem.Content>
              </PersonListItem.Container>
            </FormCheckboxItem>
          );
        }}
      />
      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Next' }}
          isDisabled={selectedPeople.length < 2}
          onPress={() =>
            form.handleSubmit(() => {
              push({ pathname: '/chat/create/group/info' });
            })()
          }
        />
      </ModalKeyboardStickyView>
    </Box>
  );
}
